/**
 * Scroll-position memory (session-scoped).
 *
 * Remembers how far down the page you were and returns you there on the next load
 * of the SAME path — within the browser session only (sessionStorage), so a fresh
 * session or a new tab starts at the top (the hero), not mid-page. Restore is an
 * INSTANT jump (never animated) even though in-page anchors smooth-scroll
 * (main.css) — returning should feel like the page was never left.
 *
 * Opt-outs that keep the behaviour from fighting other intent:
 *   - A URL hash wins: arriving at `/#contact` (the header CTA, a shared link, a
 *     bookmark) skips restore so the anchor lands where it should.
 *   - We take over `history.scrollRestoration` ('manual') so the browser's native
 *     restore doesn't double-apply.
 *   - All storage access is guarded — a privacy mode that throws on sessionStorage
 *     must never break the page.
 *
 * rAF-throttled passive scroll listener, mirroring site-header.js.
 */

const KEY = `neco:scroll:${ window.location.pathname }`;

/** sessionStorage access can throw (Safari private mode, disabled storage). */
const readSaved = () => {
	try {
		return Number( window.sessionStorage.getItem( KEY ) ) || 0;
	} catch {
		return 0;
	}
};

const writeSaved = ( y ) => {
	try {
		window.sessionStorage.setItem( KEY, String( y ) );
	} catch {
		/* storage unavailable — memory silently no-ops, page unaffected */
	}
};

// Take over from the browser's native restore so the two don't fight.
if ( 'scrollRestoration' in window.history ) {
	window.history.scrollRestoration = 'manual';
}

// Restore — unless a URL hash should win (let the anchor place the viewport).
if ( ! window.location.hash ) {
	const saved = readSaved();
	if ( saved > 0 ) {
		window.scrollTo( { top: saved, left: 0, behavior: 'auto' } ); // instant, ignores scroll-behavior:smooth
	}
}

// Persist the current position, rAF-throttled.
let ticking = false;
const save = () => {
	writeSaved( Math.round( window.scrollY ) );
	ticking = false;
};

window.addEventListener(
	'scroll',
	() => {
		if ( ! ticking ) {
			window.requestAnimationFrame( save );
			ticking = true;
		}
	},
	{ passive: true }
);
